// src/components/PDF/PDFPreviewSave.js
import React, { useState, useEffect } from 'react';
import { 
  X, 
  Download, 
  Save, 
  FileText, 
  Eye,
  Loader2,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { documentService } from '../../services/documentService';
import SimplePDFViewer from '../Documents/SimplePDFViewer';
import toast from 'react-hot-toast';
import { useAuthGuard } from '../../hooks/useAuthGuard';

const PDFPreviewSave = ({ 
  isOpen, 
  onClose, 
  pdfBlob, 
  fileName, 
  operationType = 'processed',
  onSaveComplete 
}) => {
  const { user } = useAuth();
  const { requireAuth, AuthModal } = useAuthGuard();
  const [previewDocument, setPreviewDocument] = useState(null);
  const [activeTab, setActiveTab] = useState('preview');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveData, setSaveData] = useState({
    name: fileName || 'processed.pdf',
    description: '',
    tags: []
  });

  useEffect(() => {
    if (isOpen && pdfBlob) {
      // Create preview document object for the viewer
      const url = URL.createObjectURL(pdfBlob);
      setPreviewDocument({
        id: 'preview',
        name: saveData.name,
        url: url,
        type: 'application/pdf',
        size: pdfBlob.size
      });

      return () => {
        URL.revokeObjectURL(url);
      };
    }
  }, [isOpen, pdfBlob, saveData.name]);

  useEffect(() => {
    if (isOpen) {
      setSaved(false);
      setSaveData({
        name: fileName || 'processed.pdf',
        description: '',
        tags: []
      });
    }
  }, [isOpen, fileName]);

  const handleSave = async () => {
    if (!user || !pdfBlob) {
      toast.error('Unable to save document');
      return;
    }

    if (!saveData.name.trim()) {
      toast.error('Please enter a document name');
      return;
    }

    setSaving(true);
    try {
      // Create a File object from the blob
      const file = new File([pdfBlob], saveData.name, { type: 'application/pdf' });
      
      // Upload file to storage
      const uploadResult = await documentService.uploadFile(file, user.uid, 'processed-pdfs');
      
      // Create document record
      const documentData = {
        name: saveData.name,
        type: 'application/pdf',
        size: pdfBlob.size,
        url: uploadResult.url,
        path: uploadResult.path,
        description: saveData.description,
        tags: saveData.tags,
        metadata: {
          operationType: operationType,
          originalFileName: fileName,
          processedAt: new Date().toISOString()
        }
      };
      
      const documentId = await documentService.createDocument(documentData, user.uid);
      
      setSaved(true);
      toast.success('PDF saved to your library successfully!');
      
      if (onSaveComplete) {
        onSaveComplete(documentId, documentData);
      }
      
    } catch (error) {
      console.error('Error saving PDF:', error);
      toast.error('Failed to save PDF: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = () => {
    if (!pdfBlob) return;

    requireAuth(() => {
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = saveData.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('PDF downloaded successfully!');
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      {AuthModal}
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {operationType.charAt(0).toUpperCase() + operationType.slice(1)} PDF
            </h2>
            <p className="text-sm text-gray-600">
              {formatFileSize(pdfBlob?.size || 0)} • Ready for preview, save, or download
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('preview')}
            className={`px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'preview'
                ? 'border-b-2 border-[#232751] text-[#232751]'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Eye className="w-4 h-4 mr-2 inline" />
            Preview
          </button>
          <button
            onClick={() => setActiveTab('save')}
            className={`px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'save'
                ? 'border-b-2 border-[#232751] text-[#232751]'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Save className="w-4 h-4 mr-2 inline" />
            Save to Library
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {activeTab === 'preview' && (
            <div className="h-full">
              {previewDocument ? (
                <SimplePDFViewer document={previewDocument} />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-600">Loading PDF preview...</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'save' && (
            <div className="p-6 space-y-6">
              {saved ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    PDF Saved Successfully!
                  </h3>
                  <p className="text-gray-600">
                    Your processed PDF has been saved to your document library.
                  </p>
                </div>
              ) : (
                <div className="max-w-2xl mx-auto space-y-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      Save PDF to Your Library
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Add this processed PDF to your document library for easy access later.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label htmlFor="docName" className="block text-sm font-medium text-gray-700 mb-2">
                        Document Name *
                      </label>
                      <input
                        type="text"
                        id="docName"
                        value={saveData.name}
                        onChange={(e) => setSaveData(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#232751] focus:border-transparent"
                        placeholder="Enter document name"
                      />
                    </div>

                    <div>
                      <label htmlFor="docDescription" className="block text-sm font-medium text-gray-700 mb-2">
                        Description (Optional)
                      </label>
                      <textarea
                        id="docDescription"
                        value={saveData.description}
                        onChange={(e) => setSaveData(prev => ({ ...prev, description: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#232751] focus:border-transparent"
                        rows={3}
                        placeholder="Add a description for this document"
                      />
                    </div>

                    <div>
                      <label htmlFor="docTags" className="block text-sm font-medium text-gray-700 mb-2">
                        Tags (Optional)
                      </label>
                      <input
                        type="text"
                        id="docTags"
                        value={saveData.tags.join(', ')}
                        onChange={(e) => setSaveData(prev => ({ 
                          ...prev, 
                          tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag) 
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#232751] focus:border-transparent"
                        placeholder="Enter tags separated by commas"
                      />
                    </div>
                  </div>

                  <div className="bg-[#232751]/5 border border-[#232751]/20 rounded-lg p-4">
                    <div className="flex items-start">
                      <AlertCircle className="w-5 h-5 text-[#232751] mt-0.5 mr-3 flex-shrink-0" />
                      <div className="text-sm text-[#232751]/80">
                        <p className="font-medium mb-1">Document Information:</p>
                        <ul className="space-y-1">
                          <li>• Operation: {operationType.charAt(0).toUpperCase() + operationType.slice(1)}</li>
                          <li>• File Size: {formatFileSize(pdfBlob?.size || 0)}</li>
                          <li>• Format: PDF</li>
                          <li>• This will count towards your storage quota</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <FileText className="w-4 h-4" />
            <span>{saveData.name}</span>
            <span>•</span>
            <span>{formatFileSize(pdfBlob?.size || 0)}</span>
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={handleDownload}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </button>
            
            {activeTab === 'save' && !saved && (
              <button
                onClick={handleSave}
                disabled={saving || !saveData.name.trim()}
                className="px-4 py-2 bg-[#232751] text-white rounded-lg hover:bg-[#1b1f43] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save to Library
                  </>
                )}
              </button>
            )}
            
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {saved ? 'Close' : 'Cancel'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PDFPreviewSave;
